import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, getToken } from '@/utils/api';
import { useToast } from '@/components/Toast';
import BillsOnboarding from '@/components/BillsOnboarding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Step definitions
const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to GuapGoals! 🎉',
    description: 'Let\'s set up your budget in just a few minutes.',
    icon: '👋'
  },
  {
    id: 'choice',
    title: 'How would you like to start?',
    description: 'Choose a template for a quick start, or enter everything manually.',
    icon: '🚀'
  },
  {
    id: 'income',
    title: 'Step 1: Your Income 💵',
    description: 'Tell us about your paychecks and other income sources.',
    icon: '💰',
    tips: [
      'Add each paycheck you receive regularly',
      'Include side hustles, freelance work, etc.',
      'You can always add more later!'
    ]
  },
  {
    id: 'bills',
    title: 'Step 2: Your Bills 📝',
    description: 'Add your monthly bills and expenses.',
    icon: '📋',
    tips: [
      '⚠️ DO NOT add loans or credit cards here!',
      'Add rent, utilities, subscriptions, insurance',
      'Loans & credit cards go in the Debt Calculator (next step)'
    ],
    warning: 'Loans and credit cards should be added to the Debt Calculator, not here. They will automatically appear in your bills after!'
  },
  {
    id: 'debt',
    title: 'Step 3: Your Debts ☃️',
    description: 'Add your loans and credit cards to create a payoff plan.',
    icon: '💳',
    tips: [
      'Add ALL your debts: credit cards, student loans, car loans, etc.',
      'We\'ll create a smart payoff plan for you',
      'These will automatically sync to your Bills!'
    ]
  },
  {
    id: 'goals',
    title: 'Step 4: Savings Goals 🎯',
    description: 'What are you saving for?',
    icon: '🎯',
    tips: [
      'Set goals for vacation, new car, house down payment, etc.',
      'Start building your Savings Stash today!',
      'Every dollar has a purpose'
    ]
  },
  {
    id: 'emergency',
    title: 'Step 5: Savings Stash 💰',
    description: 'Build your savings, secure your future.',
    icon: '💰',
    tips: [
      'Experts recommend 3-6 months of expenses saved',
      'We calculate your target based on your bills',
      'Start small and build up over time'
    ]
  },
  {
    id: 'complete',
    title: 'You\'re All Set! 🎉',
    description: 'Your budget is ready. Let\'s start building wealth!',
    icon: '🏆'
  }
];

// Template options
const TEMPLATES = [
  {
    id: 'single',
    name: 'Single Adult',
    icon: '👤',
    description: 'Basic setup for one person',
    bills: ['Rent/Mortgage', 'Utilities', 'Phone', 'Internet', 'Groceries', 'Transportation', 'Insurance'],
    income: ['Primary Job']
  },
  {
    id: 'couple',
    name: 'Couple/Partners',
    icon: '👫',
    description: 'Two incomes, shared expenses',
    bills: ['Rent/Mortgage', 'Utilities', 'Phone (2)', 'Internet', 'Groceries', 'Transportation', 'Insurance', 'Date Night'],
    income: ['Partner 1 Job', 'Partner 2 Job']
  },
  {
    id: 'family',
    name: 'Family',
    icon: '👨‍👩‍👧‍👦',
    description: 'Family with children',
    bills: ['Rent/Mortgage', 'Utilities', 'Phone', 'Internet', 'Groceries', 'Transportation', 'Insurance', 'Childcare', 'Kids Activities'],
    income: ['Primary Job', 'Secondary Job']
  },
  {
    id: 'student',
    name: 'Student',
    icon: '🎓',
    description: 'College student budget',
    bills: ['Rent', 'Utilities', 'Phone', 'Groceries', 'Transportation', 'Subscriptions'],
    income: ['Part-time Job', 'Financial Aid']
  },
  {
    id: 'manual',
    name: 'Start from Scratch',
    icon: '✏️',
    description: 'Enter everything manually',
    bills: [],
    income: []
  }
];

function Onboarding() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [completedSteps, setCompletedSteps] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [incomes, setIncomes] = useState([]);
  const [bills, setBills] = useState([]);
  const [debts, setDebts] = useState([]);
  const [goals, setGoals] = useState([]);
  
  // New item forms
  const [newIncome, setNewIncome] = useState({ name: '', amount: '' });
  const [newBill, setNewBill] = useState({ name: '', amount: '', category: '', due_day: '', frequency: 'Monthly', reminder_days: [] });
  const [newDebt, setNewDebt] = useState({ name: '', balance: '', apr: '', minimum_payment: '' });
  const [newGoal, setNewGoal] = useState({ name: '', target_amount: '' });

  // Check if user has completed onboarding before
  useEffect(() => {
    const hasOnboarded = localStorage.getItem('guapgoals_onboarded');
    if (hasOnboarded === 'true') {
      // User has already onboarded, but they can still access this page
    }
  }, []);

  const step = STEPS[currentStep];

  const handleTemplateSelect = async (template) => {
    setSelectedTemplate(template);
    
    if (template.id !== 'manual') {
      // Pre-fill with template data - bills need user to set category and due_day
      setIncomes(template.income.map((name, i) => ({ id: `temp-${i}`, name, amount: 0 })));
      // Bills from templates should have blank category and due_day for user to fill in
      setBills(template.bills.map((name, i) => ({ id: `temp-${i}`, name, amount: 0, category: '', due_day: '', frequency: 'Monthly', reminder_days: [] })));
    }
    
    // Move to income step
    setCurrentStep(2);
  };

  const addIncome = () => {
    if (!newIncome.name || !newIncome.amount) return;
    
    setIncomes([...incomes, {
      id: `new-${Date.now()}`,
      name: newIncome.name,
      amount: parseFloat(newIncome.amount)
    }]);
    setNewIncome({ name: '', amount: '' });
    toast.success('Income added!');
  };

  const removeIncome = (id) => {
    setIncomes(incomes.filter(i => i.id !== id));
  };

  const addBill = () => {
    if (!newBill.name || !newBill.amount || !newBill.category || !newBill.due_day) {
      toast.warning('Please fill in all required fields');
      return;
    }
    
    setBills([...bills, {
      id: `new-${Date.now()}`,
      name: newBill.name,
      amount: parseFloat(newBill.amount),
      category: newBill.category,
      due_day: parseInt(newBill.due_day),
      frequency: newBill.frequency,
      reminder_days: newBill.reminder_days || []
    }]);
    setNewBill({ name: '', amount: '', category: '', due_day: '', frequency: 'Monthly', reminder_days: [] });
    toast.success('Bill added!');
  };

  const removeBill = (id) => {
    setBills(bills.filter(b => b.id !== id));
  };

  const addDebt = () => {
    if (!newDebt.name || !newDebt.balance) return;
    
    setDebts([...debts, {
      id: `new-${Date.now()}`,
      name: newDebt.name,
      balance: parseFloat(newDebt.balance),
      apr: parseFloat(newDebt.apr) || 0,
      minimum_payment: parseFloat(newDebt.minimum_payment) || 0
    }]);
    setNewDebt({ name: '', balance: '', apr: '', minimum_payment: '' });
    toast.success('Debt added!');
  };

  const removeDebt = (id) => {
    setDebts(debts.filter(d => d.id !== id));
  };

  const addGoal = () => {
    if (!newGoal.name || !newGoal.target_amount) return;
    
    setGoals([...goals, {
      id: `new-${Date.now()}`,
      name: newGoal.name,
      target_amount: parseFloat(newGoal.target_amount)
    }]);
    setNewGoal({ name: '', target_amount: '' });
    toast.success('Goal added!');
  };

  const removeGoal = (id) => {
    setGoals(goals.filter(g => g.id !== id));
  };

  const markStepComplete = () => {
    setCompletedSteps({ ...completedSteps, [step.id]: true });
  };

  // Scroll to top helper
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveAndContinue = async () => {
    setLoading(true);
    
    try {
      const token = getToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      // Save data based on current step
      if (step.id === 'income' && incomes.length > 0) {
        // Save income as recurring templates (paychecks)
        for (const income of incomes) {
          if (!income.id.startsWith('temp-') || income.amount > 0) {
            await fetch(`${API}/income/templates`, {
              method: 'POST',
              headers,
              credentials: 'include',
              body: JSON.stringify({
                name: income.name,
                default_amount: income.amount
              })
            });
          }
        }
        toast.success('Income saved successfully!');
      }
      
      if (step.id === 'bills' && bills.length > 0) {
        // Save bills
        for (const bill of bills) {
          if (!bill.id.startsWith('temp-') || bill.amount > 0) {
            await fetch(`${API}/bills`, {
              method: 'POST',
              headers,
              credentials: 'include',
              body: JSON.stringify({
                name: bill.name,
                amount: bill.amount,
                category: bill.category,
                due_day: bill.due_day,
                frequency: bill.frequency || 'Monthly',
                reminder_days: bill.reminder_days || []
              })
            });
          }
        }
        toast.success('Bills saved successfully!');
      }
      
      if (step.id === 'debt' && debts.length > 0) {
        // Save debts
        for (const debt of debts) {
          await fetch(`${API}/debt`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({
              name: debt.name,
              balance: debt.balance,
              apr: debt.apr,
              min_payment: debt.minimum_payment,
              due_day: 1
            })
          });
        }
        toast.success('Debts saved successfully!');
      }
      
      if (step.id === 'goals' && goals.length > 0) {
        // Save goals
        for (const goal of goals) {
          await fetch(`${API}/goals`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify({
              name: goal.name,
              target_amount: goal.target_amount,
              current_amount: 0
            })
          });
        }
        toast.success('Goals saved successfully!');
      }
      
      markStepComplete();
      setCurrentStep(currentStep + 1);
      scrollToTop();
      
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const skipStep = () => {
    markStepComplete();
    setCurrentStep(currentStep + 1);
    scrollToTop();
  };

  const goToNextStep = () => {
    markStepComplete();
    setCurrentStep(currentStep + 1);
    scrollToTop();
  };

  const goToPrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      scrollToTop();
    }
  };

  const finishOnboarding = async () => {
    // Mark onboarding as complete on the backend
    try {
      const token = getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      await fetch(`${API}/auth/complete-onboarding`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error marking onboarding complete:', error);
    }
    
    localStorage.setItem('guapgoals_onboarded', 'true');
    toast.success('Setup complete! Welcome to GuapGoals!');
    navigate('/app');
  };

  const goToEmergencyFund = async () => {
    // Mark onboarding as complete on the backend
    try {
      const token = getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      await fetch(`${API}/auth/complete-onboarding`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error marking onboarding complete:', error);
    }
    
    localStorage.setItem('guapgoals_onboarded', 'true');
    navigate('/app/emergency-fund');
  };

  const skipOnboarding = async () => {
    // Mark onboarding as complete on the backend
    try {
      const token = getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      await fetch(`${API}/auth/complete-onboarding`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
    } catch (error) {
      console.error('Error marking onboarding complete:', error);
    }
    
    localStorage.setItem('guapgoals_onboarded', 'true');
    toast.info('You can access the walkthrough anytime from the menu.');
    navigate('/app');
  };

  // Calculate progress
  const progress = Math.round((currentStep / (STEPS.length - 1)) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200 z-50">
        <div 
          className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Skip Button */}
      {currentStep < STEPS.length - 1 && currentStep > 0 && (
        <button
          onClick={skipOnboarding}
          className="fixed top-4 right-4 text-gray-500 hover:text-gray-700 text-sm font-medium z-50 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full shadow-sm"
        >
          Skip Setup →
        </button>
      )}

      <div className="max-w-2xl mx-auto px-4 py-8 md:py-16">
        {/* Step Indicator */}
        {currentStep > 1 && currentStep < STEPS.length - 1 && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {STEPS.slice(2, -1).map((s, i) => (
              <div
                key={s.id}
                className={`w-3 h-3 rounded-full transition-colors ${
                  i + 2 < currentStep ? 'bg-emerald-500' :
                  i + 2 === currentStep ? 'bg-emerald-500 ring-4 ring-emerald-200' :
                  'bg-gray-300'
                }`}
              />
            ))}
          </div>
        )}

        {/* Welcome Step */}
        {step.id === 'welcome' && (
          <div className="text-center animate-fade-in">
            <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <span className="text-5xl">{step.icon}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{step.title}</h1>
            <p className="text-lg text-gray-600 mb-8">{step.description}</p>
            <p className="text-gray-500 mb-8">This quick setup will help you get the most out of GuapGoals.</p>
            
            <div className="space-y-3">
              <button
                onClick={() => setCurrentStep(1)}
                className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-2xl font-bold text-lg hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg"
              >
                Let's Get Started! 🚀
              </button>
              <button
                onClick={skipOnboarding}
                className="w-full py-3 text-gray-500 hover:text-gray-700 font-medium"
              >
                Skip for now, I'll explore on my own
              </button>
            </div>
          </div>
        )}

        {/* Template Choice Step */}
        {step.id === 'choice' && (
          <div className="animate-fade-in">
            {/* Back Button */}
            <button
              onClick={goToPrevStep}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6"
              data-testid="back-btn-choice"
            >
              ← Back
            </button>
            
            <div className="text-center mb-8">
              <span className="text-5xl mb-4 block">{step.icon}</span>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{step.title}</h1>
              <p className="text-gray-600">{step.description}</p>
            </div>
            
            <div className="grid gap-4">
              {TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateSelect(template)}
                  className="flex items-center gap-4 p-4 bg-white rounded-2xl border-2 border-gray-200 hover:border-emerald-400 hover:shadow-lg transition-all text-left group"
                >
                  <div className="w-14 h-14 bg-gray-100 group-hover:bg-emerald-100 rounded-xl flex items-center justify-center text-2xl transition-colors">
                    {template.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900">{template.name}</h3>
                    <p className="text-sm text-gray-500">{template.description}</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-emerald-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Income Step */}
        {step.id === 'income' && (
          <div className="animate-fade-in">
            <div className="text-center mb-6">
              <span className="text-4xl mb-2 block">{step.icon}</span>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h1>
              <p className="text-gray-600">{step.description}</p>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-xl p-4 mb-6">
              <p className="font-medium text-blue-800 mb-2">💡 Tips:</p>
              <ul className="text-sm text-blue-700 space-y-1">
                {step.tips.map((tip, i) => (
                  <li key={i}>• {tip}</li>
                ))}
              </ul>
            </div>

            {/* Income List */}
            {incomes.length > 0 && (
              <div className="space-y-2 mb-6">
                {incomes.map((income) => (
                  <div key={income.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200">
                    <span className="font-medium">{income.name}</span>
                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        value={income.amount || ''}
                        onChange={(e) => {
                          setIncomes(incomes.map(i => 
                            i.id === income.id ? { ...i, amount: parseFloat(e.target.value) || 0 } : i
                          ));
                        }}
                        placeholder="Amount"
                        className="w-28 px-3 py-2 border rounded-lg text-right text-base"
                      />
                      <button onClick={() => removeIncome(income.id)} className="text-red-500 hover:text-red-700 p-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Income Form */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="font-medium text-gray-700 mb-3">Add Income Source</p>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newIncome.name}
                  onChange={(e) => setNewIncome({ ...newIncome, name: e.target.value })}
                  placeholder="Income name (e.g., Main Job, Side Hustle)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base"
                />
                <div className="flex gap-3">
                  <input
                    type="number"
                    value={newIncome.amount}
                    onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
                    placeholder="Amount per paycheck"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-base"
                  />
                  <button
                    onClick={addIncome}
                    disabled={!newIncome.name || !newIncome.amount}
                    className="px-6 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={goToPrevStep}
                className="py-3 px-4 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                ← Back
              </button>
              <button
                onClick={skipStep}
                className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                Skip for Now
              </button>
              <button
                onClick={saveAndContinue}
                disabled={loading}
                className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 disabled:bg-gray-300"
              >
                {loading ? 'Saving...' : 'Save & Continue →'}
              </button>
            </div>
          </div>
        )}

        {/* Bills Step - New Hybrid Plaid + Gamified Flow */}
        {step.id === 'bills' && (
          <BillsOnboarding
            existingBills={bills}
            onComplete={(result) => {
              // Update local bills state with the results
              if (result.bills) {
                setBills(result.bills);
              }
              // Move to next step
              goToNextStep();
            }}
            onSkip={skipStep}
          />
        )}

        {/* Debt Step */}
        {step.id === 'debt' && (
          <div className="animate-fade-in">
            <div className="text-center mb-6">
              <span className="text-4xl mb-2 block">{step.icon}</span>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h1>
              <p className="text-gray-600">{step.description}</p>
            </div>

            {/* Tips */}
            <div className="bg-purple-50 rounded-xl p-4 mb-6">
              <p className="font-medium text-purple-800 mb-2">💳 Add your debts here:</p>
              <ul className="text-sm text-purple-700 space-y-1">
                {step.tips.map((tip, i) => (
                  <li key={i}>• {tip}</li>
                ))}
              </ul>
            </div>

            {/* Debts List */}
            {debts.length > 0 && (
              <div className="space-y-2 mb-6">
                {debts.map((debt) => (
                  <div key={debt.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200">
                    <div>
                      <span className="font-medium">{debt.name}</span>
                      <span className="text-sm text-gray-500 ml-2">{debt.apr}% APR</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-red-600">${debt.balance.toLocaleString()}</span>
                      <button onClick={() => removeDebt(debt.id)} className="text-red-500 hover:text-red-700 p-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Debt Form */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="font-medium text-gray-700 mb-3">Add a Debt</p>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newDebt.name}
                  onChange={(e) => setNewDebt({ ...newDebt, name: e.target.value })}
                  placeholder="Debt name (e.g., Chase Visa, Car Loan)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="number"
                    value={newDebt.balance}
                    onChange={(e) => setNewDebt({ ...newDebt, balance: e.target.value })}
                    placeholder="Total balance"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base"
                  />
                  <input
                    type="number"
                    value={newDebt.apr}
                    onChange={(e) => setNewDebt({ ...newDebt, apr: e.target.value })}
                    placeholder="APR %"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base"
                  />
                </div>
                <div className="flex gap-3">
                  <input
                    type="number"
                    value={newDebt.minimum_payment}
                    onChange={(e) => setNewDebt({ ...newDebt, minimum_payment: e.target.value })}
                    placeholder="Minimum payment"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-base"
                  />
                  <button
                    onClick={addDebt}
                    disabled={!newDebt.name || !newDebt.balance}
                    className="px-6 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={goToPrevStep}
                className="py-3 px-4 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                ← Back
              </button>
              <button
                onClick={skipStep}
                className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                Skip for Now
              </button>
              <button
                onClick={saveAndContinue}
                disabled={loading}
                className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 disabled:bg-gray-300"
              >
                {loading ? 'Saving...' : 'Save & Continue →'}
              </button>
            </div>
          </div>
        )}

        {/* Goals Step */}
        {step.id === 'goals' && (
          <div className="animate-fade-in">
            <div className="text-center mb-6">
              <span className="text-4xl mb-2 block">{step.icon}</span>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h1>
              <p className="text-gray-600">{step.description}</p>
            </div>

            {/* Tips */}
            <div className="bg-green-50 rounded-xl p-4 mb-6">
              <p className="font-medium text-green-800 mb-2">🎯 Goal Ideas:</p>
              <ul className="text-sm text-green-700 space-y-1">
                {step.tips.map((tip, i) => (
                  <li key={i}>• {tip}</li>
                ))}
              </ul>
            </div>

            {/* Goals List */}
            {goals.length > 0 && (
              <div className="space-y-2 mb-6">
                {goals.map((goal) => (
                  <div key={goal.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200">
                    <span className="font-medium">{goal.name}</span>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-green-600">${goal.target_amount.toLocaleString()}</span>
                      <button onClick={() => removeGoal(goal.id)} className="text-red-500 hover:text-red-700 p-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Goal Form */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="font-medium text-gray-700 mb-3">Add a Goal</p>
              <div className="space-y-3">
                <input
                  type="text"
                  value={newGoal.name}
                  onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
                  placeholder="Goal name (e.g., Vacation, New Car)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base"
                />
                <div className="flex gap-3">
                  <input
                    type="number"
                    value={newGoal.target_amount}
                    onChange={(e) => setNewGoal({ ...newGoal, target_amount: e.target.value })}
                    placeholder="Target amount"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-base"
                  />
                  <button
                    onClick={addGoal}
                    disabled={!newGoal.name || !newGoal.target_amount}
                    className="px-6 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={goToPrevStep}
                className="py-3 px-4 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                ← Back
              </button>
              <button
                onClick={skipStep}
                className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                Skip for Now
              </button>
              <button
                onClick={saveAndContinue}
                disabled={loading}
                className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 disabled:bg-gray-300"
              >
                {loading ? 'Saving...' : 'Save & Continue →'}
              </button>
            </div>
          </div>
        )}

        {/* Savings Stash Step */}
        {step.id === 'emergency' && (
          <div className="animate-fade-in">
            <div className="text-center mb-6">
              <span className="text-4xl mb-2 block">{step.icon}</span>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h1>
              <p className="text-gray-600">{step.description}</p>
            </div>

            {/* Tips */}
            <div className="bg-emerald-50 rounded-xl p-4 mb-6">
              <p className="font-medium text-emerald-800 mb-2">💰 Why saving matters:</p>
              <ul className="text-sm text-emerald-700 space-y-1">
                {step.tips.map((tip, i) => (
                  <li key={i}>• {tip}</li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 text-center">
              <p className="text-gray-600 mb-4">
                Your savings target is calculated based on your monthly bills.
                Let's set it up!
              </p>
              <button
                onClick={goToEmergencyFund}
                className="px-8 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors"
              >
                Start Saving →
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={goToPrevStep}
                className="py-3 px-4 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                ← Back
              </button>
              <button
                onClick={() => { setCurrentStep(currentStep + 1); scrollToTop(); }}
                className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                Skip for Now
              </button>
            </div>
          </div>
        )}

        {/* Complete Step */}
        {step.id === 'complete' && (
          <div className="text-center animate-fade-in">
            <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <span className="text-5xl">{step.icon}</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{step.title}</h1>
            <p className="text-lg text-gray-600 mb-8">{step.description}</p>
            
            <div className="bg-emerald-50 rounded-xl p-6 mb-8">
              <p className="text-emerald-800 font-medium mb-2">What's next?</p>
              <ul className="text-sm text-emerald-700 text-left space-y-2">
                <li>✓ Check your Dashboard to see your budget overview</li>
                <li>✓ Use Ash (the chatbot) if you have any questions</li>
                <li>✓ Come back each payday to update your numbers</li>
              </ul>
            </div>
            
            <button
              onClick={finishOnboarding}
              className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-2xl font-bold text-lg hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg"
            >
              Go to Dashboard 🚀
            </button>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}

export default Onboarding;
